// src/utils/osmParser.ts

/**
 * Parser para datos de OpenStreetMap (OSM) provenientes de Overpass API.
 * Convierte la estructura cruda (nodos, vías, relaciones) en objetos
 * utilizables para generar mallas 3D.
 */

import type { LatLng, LocalPoint } from './geoConverter';

// Tipos de coordenadas y conversor compartidos con geoConverter.ts
export { GeoConverter } from './geoConverter';
export type { LatLng, LocalPoint } from './geoConverter';

// ----------------------------------------------------------------------
// Tipos de datos OSM (formato Overpass)
// ----------------------------------------------------------------------

export interface OSMNode {
  type: 'node';
  id: number;
  lat: number;
  lon: number;
  tags?: Record<string, string>;
}

export interface OSMWay {
  type: 'way';
  id: number;
  nodes: number[];
  tags?: Record<string, string>;
}

export interface OSMRelationMember {
  type: 'node' | 'way' | 'relation';
  ref: number;
  role: string; // 'outer', 'inner', 'admin_centre', etc.
}

export interface OSMRelation {
  type: 'relation';
  id: number;
  members: OSMRelationMember[];
  tags?: Record<string, string>;
}

export type OSMElement = OSMNode | OSMWay | OSMRelation;

export interface OSMResponse {
  version: number;
  generator: string;
  osm3s?: {
    timestamp_osm_base?: string;
    copyright?: string;
  };
  elements: OSMElement[];
}

// ----------------------------------------------------------------------
// Tipos de salida procesados
// ----------------------------------------------------------------------

/** Edificio parseado con anillos exterior e interiores */
export interface ParsedBuilding {
  id: number | string;   // string cuando es un anillo sintético de una relación
  type: 'way' | 'relation';
  outerRing: LatLng[];          // anillo exterior
  innerRings: LatLng[][];       // huecos (patios)
  tags: Record<string, string>;
  height: number;               // metros
  levels?: number;
  name?: string;
  buildingType?: string;
}

/** Carretera parseada */
export interface ParsedRoad {
  id: number;
  coordinates: LatLng[];
  tags: Record<string, string>;
  highwayType: string;
  lanes: number;
  oneway: boolean;
  name?: string;
  width?: number;               // ancho estimado en metros
}

export interface ParsedOSMData {
  nodes: Map<number, OSMNode>;
  ways: OSMWay[];
  relations: OSMRelation[];
  buildings: ParsedBuilding[];
  roads: ParsedRoad[];
}

// ----------------------------------------------------------------------
// Parser principal
// ----------------------------------------------------------------------

/**
 * Parsea la respuesta completa de Overpass.
 * @param data - Respuesta JSON de Overpass API
 * @returns Datos estructurados: nodos, ways, relations, edificios y calles
 */
export function parseOSMData(data: OSMResponse): ParsedOSMData {
  const nodes = new Map<number, OSMNode>();
  const ways: OSMWay[] = [];
  const relations: OSMRelation[] = [];

  // 1. Separar elementos por tipo
  data.elements.forEach((el) => {
    if (el.type === 'node') nodes.set(el.id, el as OSMNode);
    else if (el.type === 'way') ways.push(el as OSMWay);
    else if (el.type === 'relation') relations.push(el as OSMRelation);
  });

  // 2. Construir edificios
  const buildings: ParsedBuilding[] = [];

  // 2a. Vías simples con building
  ways.forEach((way) => {
    if (way.tags?.building) {
      const coords = getWayLatLng(way, nodes);
      const tags = way.tags ?? {};
      if (coords.length >= 3) {
        buildings.push({
          id: way.id,
          type: 'way',
          outerRing: coords,
          innerRings: [],
          tags,
          height: parseBuildingHeight(tags),
          levels: parseBuildingLevels(tags),
          name: tags.name,
          buildingType: tags.building,
        });
      }
    }
  });

  // 2b. Relaciones multipolígono de edificios
  relations.forEach((rel) => {
    if (rel.tags?.building) {
      const outerRings: LatLng[][] = [];
      const innerRings: LatLng[][] = [];
      const tags = rel.tags ?? {};

      rel.members.forEach((member) => {
        if (member.type !== 'way') return;
        const way = ways.find((w) => w.id === member.ref);
        if (!way) return;
        const coords = getWayLatLng(way, nodes);
        if (coords.length < 3) return;
        if (member.role === 'outer') outerRings.push(coords);
        else if (member.role === 'inner') innerRings.push(coords);
      });

      // Si hay al menos un anillo exterior, crear edificio(s)
      if (outerRings.length > 0) {
        // Si hay múltiples anillos exteriores, tratarlos como edificios separados
        // (algunos multipolígonos representan varios edificios contiguos)
        outerRings.forEach((ring, index) => {
          buildings.push({
            id: `${rel.id}_${index}`, // ID sintético único para cada anillo
            type: 'relation',
            outerRing: ring,
            innerRings: innerRings, // comparten huecos (simplificación)
            tags,
            height: parseBuildingHeight(tags),
            levels: parseBuildingLevels(tags),
            name: tags.name,
            buildingType: tags.building,
          });
        });
      }
    }
  });

  // 3. Construir calles
  const roads: ParsedRoad[] = [];
  ways.forEach((way) => {
    if (way.tags?.highway) {
      const coords = getWayLatLng(way, nodes);
      if (coords.length >= 2) {
        const highwayType = way.tags.highway;
        const lanes = parseInt(way.tags.lanes || '2', 10);
        roads.push({
          id: way.id,
          coordinates: coords,
          tags: way.tags,
          highwayType,
          lanes,
          oneway: way.tags.oneway === 'yes',
          name: way.tags.name,
          width: estimateRoadWidth(highwayType, lanes),
        });
      }
    }
  });

  return { nodes, ways, relations, buildings, roads };
}

// ----------------------------------------------------------------------
// Utilidades internas
// ----------------------------------------------------------------------

/**
 * Obtiene las coordenadas geográficas de una vía usando el mapa de nodos.
 */
function getWayLatLng(
  way: OSMWay,
  nodes: Map<number, OSMNode>
): LatLng[] {
  const result: LatLng[] = [];
  for (const nodeId of way.nodes) {
    const node = nodes.get(nodeId);
    if (node) {
      result.push([node.lat, node.lon]);
    }
  }
  return result;
}

/**
 * Extrae la altura en metros de un edificio a partir de sus etiquetas.
 */
export function parseBuildingHeight(tags: Record<string, string>): number {
  // Prioridad: 'height' en metros
  if (tags.height) {
    const h = parseFloat(tags.height);
    if (!isNaN(h) && h > 0) return h;
  }
  // Luego 'building:levels' (3 m por nivel)
  if (tags['building:levels']) {
    const levels = parseFloat(tags['building:levels']);
    if (!isNaN(levels) && levels > 0) return levels * 3;
  }
  // Luego 'est_height' (a veces usado)
  if (tags.est_height) {
    const h = parseFloat(tags.est_height);
    if (!isNaN(h) && h > 0) return h;
  }
  // Valores por defecto según tipo de edificio
  const type = tags.building || '';
  switch (type) {
    case 'house':
    case 'detached':
    case 'terrace':
      return 6;
    case 'apartments':
      return 15;
    case 'commercial':
    case 'retail':
      return 8;
    case 'industrial':
      return 10;
    case 'office':
      return 12;
    default:
      return 10;
  }
}

/**
 * Extrae el número de niveles de un edificio.
 */
export function parseBuildingLevels(tags: Record<string, string>): number | undefined {
  if (tags['building:levels']) {
    const levels = parseInt(tags['building:levels'], 10);
    if (!isNaN(levels) && levels > 0) return levels;
  }
  return undefined;
}

/**
 * Estima el ancho de una carretera en metros según tipo y número de carriles.
 */
export function estimateRoadWidth(
  highwayType: string,
  lanes: number
): number {
  // Ancho por carril (aprox. 3 m en zonas urbanas)
  const laneWidth = 3;
  let extraWidth = 0;

  switch (highwayType) {
    case 'motorway':
    case 'trunk':
      extraWidth = 6; // arcenes y separadores
      break;
    case 'primary':
      extraWidth = 4;
      break;
    case 'secondary':
    case 'tertiary':
      extraWidth = 3;
      break;
    case 'residential':
    case 'service':
    case 'living_street':
      extraWidth = 2;
      break;
    case 'footway':
    case 'path':
      return 2; // senderos peatonales angostos
    default:
      extraWidth = 2;
  }

  return lanes * laneWidth + extraWidth;
}

/**
 * Ordena los puntos de un anillo para que estén en sentido antihorario
 * (requerido por THREE.Shape para exteriores).
 * No implementado aquí porque no siempre es necesario; se puede agregar
 * usando el algoritmo de área.
 */
export function ensureCounterClockwise(points: LocalPoint[]): LocalPoint[] {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    sum += points[i].x * points[j].z - points[j].x * points[i].z;
  }
  // Si la suma es negativa (en coordenadas x,z con z hacia arriba), es horario
  if (sum < 0) {
    return points.reverse();
  }
  return points;
}

/**
 * Crea un shape de THREE.Shape a partir de anillos exterior e interiores.
 * Esta función depende de Three.js y debería ir en un archivo separado,
 * pero se incluye como referencia de uso.
 */
/*
import * as THREE from 'three';

export function createShapeFromRings(
  outerRing: LocalPoint[],
  innerRings: LocalPoint[][]
): THREE.Shape {
  const shape = new THREE.Shape();
  if (outerRing.length < 3) return shape;

  shape.moveTo(outerRing[0].x, outerRing[0].z);
  for (let i = 1; i < outerRing.length; i++) {
    shape.lineTo(outerRing[i].x, outerRing[i].z);
  }
  shape.closePath();

  innerRings.forEach((ring) => {
    if (ring.length < 3) return;
    const hole = new THREE.Path();
    hole.moveTo(ring[0].x, ring[0].z);
    for (let i = 1; i < ring.length; i++) {
      hole.lineTo(ring[i].x, ring[i].z);
    }
    hole.closePath();
    shape.holes.push(hole);
  });

  return shape;
}
*/